// composables/useNetworkSpeed.ts

import { ref, onMounted, onUnmounted, readonly } from 'vue'

export interface NetworkSpeedData {
  speed: number // Mbps cinsinden
  status: 'good' | 'slow' | 'very-slow' | 'offline'
  isOnline: boolean
}

export function useNetworkSpeed() {
  const networkData = ref<NetworkSpeedData>({
    speed: 0,
    status: 'good',
    isOnline: navigator.onLine
  })
  
  const showWarning = ref(false)
  const isMonitoring = ref(false)
  
  // Hız eşikleri (Mbps) - Bu değerleri ihtiyacınıza göre ayarlayın
  const SPEED_THRESHOLDS = {
    VERY_SLOW: 0.01,    // 1 Mbps'den düşük = Çok yavaş
    SLOW: 0.1,         // 5 Mbps'den düşük = Yavaş  
    GOOD: 1         // 10 Mbps'den yüksek = İyi
  }
  
  let intervalId: number | null = null
  let warningTimeoutId: number | null = null
  
  // Test için küçük bir dosya indir ve hızı hesapla
  const measureSpeed = async (): Promise<number> => {
    try {
      const startTime = performance.now()
      
      // 1MB'lık test dosyası (JSONPlaceholder kullanarak)
      const testUrl = 'https://jsonplaceholder.typicode.com/photos'
      const response = await fetch(testUrl + '?_limit=100&' + Date.now(), {
        cache: 'no-cache'
      })
      
      if (!response.ok) throw new Error('Ağ testi başarısız. Lütfen tekrar deneyin.')
      
      await response.json()
      const endTime = performance.now()
      
      const duration = (endTime - startTime) / 1000 // saniye
      const bytes = parseInt(response.headers.get('content-length') || '50000') // yaklaşık boyut
      const bitsPerSecond = (bytes * 8) / duration
      const mbps = bitsPerSecond / (1024 * 1024)
      
      return Math.max(mbps, 0.1) // Minimum 0.1 Mbps
    } catch (error) {
      console.warn('Hız testi başarısız:', error)
      return 0.1
    }
  }
  
  const updateNetworkStatus = (speed: number) => {
    let status: NetworkSpeedData['status'] = 'good'
    
    if (speed < SPEED_THRESHOLDS.VERY_SLOW) {
      status = 'very-slow'
    } else if (speed < SPEED_THRESHOLDS.SLOW) {
      status = 'slow'
    }
    
    networkData.value = {
      speed: Math.round(speed * 10) / 10,
      status,
      isOnline: navigator.onLine
    }
    
    // Her ölçümde mevcut timeout'ları temizle
    if (warningTimeoutId) {
      clearTimeout(warningTimeoutId)
      warningTimeoutId = null
    }
    
    // Uyarı gösterme mantığı
    if (status === 'very-slow') {
      // Çok yavaş - sürekli göster, kapatma izni yok
      showWarning.value = true
      // Timeout YOK - sürekli kalacak
    } else if (status === 'slow') {
      // Yavaş - göster ama kullanıcı kapatabilir
      showWarning.value = true
      // Otomatik kapanma yok, sadece kullanıcı kapatabilir
    } else {
      // İyi - uyarı gösterme
      showWarning.value = false
    }
  }
  
  const startMonitoring = () => {
    if (isMonitoring.value) return
    
    isMonitoring.value = true
    
    // Mevcut timeout'ları temizle
    if (warningTimeoutId) {
      clearTimeout(warningTimeoutId)
      warningTimeoutId = null
    }
    
    // İlk ölçüm
    measureSpeed().then(updateNetworkStatus)
    
    // 30 saniyede bir kontrol et
    intervalId = setInterval(async () => {
      const speed = await measureSpeed()
      updateNetworkStatus(speed)
    }, 60000000)
  }
  
  const stopMonitoring = () => {
    isMonitoring.value = false
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    if (warningTimeoutId) {
      clearTimeout(warningTimeoutId)
      warningTimeoutId = null
    }
  }
  
  const dismissWarning = () => {
    // Sadece 'slow' durumunda kapatma izni var
    if (networkData.value.status === 'slow') {
      showWarning.value = false
      if (warningTimeoutId) {
        clearTimeout(warningTimeoutId)
        warningTimeoutId = null
      }
    }
    // very-slow durumunda hiçbir şey yapma (kapatılamaz)
    // offline durumunda da kapatılamaz
  }
  
  // Online/offline durumunu dinle
  const handleOnline = () => {
    networkData.value.isOnline = true
    // Mevcut timeout'ları temizle
    if (warningTimeoutId) {
      clearTimeout(warningTimeoutId)
      warningTimeoutId = null
    }
    if (isMonitoring.value) {
      measureSpeed().then(updateNetworkStatus)
    }
  }
  
  const handleOffline = () => {
    networkData.value = {
      ...networkData.value,
      isOnline: false,
      status: 'offline'
    }
    // Offline durumunda sürekli göster
    showWarning.value = true
  }
  
  onMounted(() => {
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    startMonitoring()
  })
  
  onUnmounted(() => {
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
    stopMonitoring()
  })
  
  return {
    networkData: readonly(networkData),
    showWarning: readonly(showWarning),
    isMonitoring: readonly(isMonitoring),
    startMonitoring,
    stopMonitoring,
    dismissWarning
  }
}
